import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

function iso(d) { return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString(); }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate()+n); return x; }
function fmtDay(ds) { const d = new Date(ds); return d.getDate(); }
function dowShort(ds) { return new Date(ds).toLocaleDateString(undefined,{weekday:'short'})[0]; }

export default function AdminRunGrid() {
  const [anchor, setAnchor] = useState(() => { const d = new Date(); d.setHours(0,0,0,0); return d; });
  const [days] = useState(21);
  const [grid, setGrid] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // { booking, runId, start, end }
  const [runs, setRuns] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/availability/grid', { params: { start: iso(anchor), days } });
      setGrid(data);
      setRuns(data.runs);
    } catch { toast.error('Failed to load grid'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [anchor.getTime()]);

  const openBooking = async (bookingId, run, ds) => {
    const { data } = await api.get(`/bookings/${bookingId}`);
    setEditing({ booking: data, runId: data.run_id, start: data.start.slice(0,10), end: data.end.slice(0,10) });
  };

  const saveMove = async () => {
    try {
      await api.patch(`/bookings/${editing.booking.id}/move`, {
        run_id: editing.runId,
        start: new Date(editing.start + 'T00:00').toISOString(),
        end: new Date(editing.end + 'T00:00').toISOString(),
      });
      toast.success('Booking rescheduled');
      setEditing(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Move failed'); }
  };

  const setStatus = async (status) => {
    try {
      await api.patch(`/bookings/${editing.booking.id}/status`, { status });
      toast.success(`Marked ${status}`);
      setEditing(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
  };

  const cellTone = (cell) => {
    if (!cell) return { bg: '#0F172A', border: '#1E293B', text: '#94a3b8' };
    if (cell.status === 'PAID' || cell.status === 'CHECKED_IN') return { bg: '#1B3A87', border: '#2B6CB0', text: '#fff' };
    if (cell.status === 'PENDING_PAYMENT') return { bg: '#B45309', border: '#F59E0B', text: '#fff' };
    return { bg: '#475569', border: '#64748B', text: '#fff' };
  };

  return (
    <div className='p-6 lg:p-8'>
      <div className='flex items-center justify-between mb-4'>
        <div>
          <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-[#C8102E]'>Scheduler</div>
          <h1 className='font-display text-3xl font-bold text-slate-900 mt-1'>Run Grid · Matrix View</h1>
          <p className='text-slate-500 text-sm mt-1'>{days} days from {anchor.toLocaleDateString()}. Click any booked cell to reschedule.</p>
        </div>
        <div className='flex gap-2'>
          <Button size='sm' variant='outline' className='border-slate-200 text-slate-600 hover:bg-slate-50' onClick={() => setAnchor(addDays(anchor, -7))} data-testid='grid-prev-button'><ChevronLeft className='w-4 h-4' /></Button>
          <Button size='sm' variant='outline' className='border-slate-200 text-slate-600 hover:bg-slate-50' onClick={() => { const d = new Date(); d.setHours(0,0,0,0); setAnchor(d); }} data-testid='grid-today-button'>Today</Button>
          <Button size='sm' variant='outline' className='border-slate-200 text-slate-600 hover:bg-slate-50' onClick={() => setAnchor(addDays(anchor, 7))} data-testid='grid-next-button'><ChevronRight className='w-4 h-4' /></Button>
        </div>
      </div>

      <Card className='p-3 bg-white border-slate-200 overflow-x-auto'>
        {loading && !grid ? (
          <div className='py-20 flex items-center justify-center text-slate-500'><Loader2 className='w-5 h-5 animate-spin mr-2' /> Loading…</div>
        ) : grid && (
          <table className='min-w-full text-xs' data-testid='run-grid-table'>
            <thead>
              <tr>
                <th className='sticky left-0 bg-white z-10 text-left p-2 border-b border-slate-200 font-semibold'>Run</th>
                {grid.dates.map(ds => {
                  const d = new Date(ds);
                  const isWeekend = [0,6].includes(d.getDay());
                  return <th key={ds} className={`p-1.5 border-b border-slate-200 font-mono font-semibold tabular-nums ${isWeekend ? 'text-slate-500' : 'text-slate-600'}`}>
                    <div className='text-[9px] uppercase tracking-wider opacity-70'>{dowShort(ds)}</div>
                    <div>{fmtDay(ds)}</div>
                  </th>;
                })}
              </tr>
            </thead>
            <tbody>
              {runs.map(r => (
                <tr key={r.id}>
                  <td className='sticky left-0 bg-white z-10 p-2 border-b border-slate-200'>
                    <div className='font-mono font-semibold text-slate-900 text-xs'>{r.name}</div>
                    <div className='text-[9px] uppercase tracking-wider text-slate-500'>{r.type}</div>
                  </td>
                  {grid.dates.map(ds => {
                    const cell = grid.grid[r.id][ds];
                    const t = cellTone(cell);
                    return (
                      <td key={ds} className='p-0.5 border-b border-slate-200'>
                        {cell ? (
                          <button className='w-full h-9 rounded text-[10px] font-semibold flex items-center justify-center truncate px-1' style={{background: t.bg, border: `1px solid ${t.border}`, color: t.text}} onClick={() => openBooking(cell.booking_id, r, ds)} data-testid={`grid-cell-${r.name}-${ds}`} title={`${cell.dog_name} · ${cell.status}`}>
                            {cell.is_start ? cell.dog_name?.substring(0,4) : '—'}
                          </button>
                        ) : (
                          <div className='w-full h-9 rounded run-cell border border-slate-200 bg-slate-50' />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <div className='flex gap-4 mt-4 text-xs text-slate-500'>
        <div className='flex items-center gap-1.5'><div className='w-3 h-3 rounded' style={{background:'#1B3A87'}} /> Paid / Checked-in</div>
        <div className='flex items-center gap-1.5'><div className='w-3 h-3 rounded' style={{background:'#B45309'}} /> Pending payment</div>
        <div className='flex items-center gap-1.5'><div className='w-3 h-3 rounded' style={{background:'#475569'}} /> Hold</div>
        <div className='flex items-center gap-1.5'><div className='w-3 h-3 rounded border border-slate-200' style={{background:'#F8FAFC'}} /> Available</div>
      </div>

      <Dialog open={!!editing} onOpenChange={(v)=>!v && setEditing(null)}>
        <DialogContent className='max-w-md'>
          <DialogHeader><DialogTitle>Booking · {editing?.booking.dog_name}</DialogTitle></DialogHeader>
          {editing && (
            <div className='space-y-3'>
              <div className='grid grid-cols-2 gap-3 text-sm'>
                <div><span className='text-slate-500'>Client:</span> {editing.booking.client_name}</div>
                <div><span className='text-slate-500'>Status:</span> <Badge>{editing.booking.status.replace('_',' ')}</Badge></div>
                <div><span className='text-slate-500'>Amount:</span> ${editing.booking.amount.toFixed(2)}</div>
                <div><span className='text-slate-500'>Run:</span> <span className='font-mono'>{editing.booking.run_name}</span></div>
              </div>
              <div className='border-t pt-3 space-y-2'>
                <Label>Move to Run</Label>
                <select className='w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white text-black' value={editing.runId} onChange={(e)=>setEditing({...editing, runId: e.target.value})} data-testid='move-run-select'>
                  {runs.map(r => <option key={r.id} value={r.id}>{r.name} ({r.type})</option>)}
                </select>
                <div className='grid grid-cols-2 gap-2'>
                  <div><Label>Start</Label><Input type='date' value={editing.start} onChange={(e)=>setEditing({...editing, start: e.target.value})} data-testid='move-start-input' /></div>
                  <div><Label>End</Label><Input type='date' value={editing.end} onChange={(e)=>setEditing({...editing, end: e.target.value})} data-testid='move-end-input' /></div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className='gap-2 flex-wrap'>
            <Button size='sm' variant='outline' onClick={()=>setStatus('CHECKED_IN')} data-testid='status-checkin'>Check In</Button>
            <Button size='sm' variant='outline' onClick={()=>setStatus('CHECKED_OUT')} data-testid='status-checkout'>Check Out</Button>
            <Button size='sm' variant='outline' onClick={()=>setStatus('CANCELLED')} data-testid='status-cancel'>Cancel</Button>
            <Button onClick={saveMove} className='bg-[#1B3A87] hover:bg-[#16306F] text-slate-900' data-testid='move-save-button'>Save Move</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
